import type { Command } from '@/core/Command';
import type { Vec3 } from '@/flora/FloraInstance';
import { isMovable, type StaffageChange, type StaffageLayer, type StaffageRecord } from './StaffageLayer';
import type { TransformMode, TransformSubject } from './TransformTool';

/**
 * Creatures and objects, as the transform gesture sees them.
 *
 * Two differences from the flora subject, and both are the same difference twice:
 *
 * - **`y` is left alone.** A plant is re-seated onto the terrain as it is dragged, because its `y`
 *   *is* the ground height. A creature's and an object's `y` is an offset their worlds add to a
 *   ground height they sample themselves, so writing terrain into it would drop a perched robin out
 *   of its tree and sink a bench by exactly the height of the ground it is standing on.
 * - **There is no maturity.** A bench does not grow, and neither does a rabbit — see `FaunaInstance`
 *   for the longer version. `M` over a selection of these picks up nothing and does nothing, which
 *   is the honest outcome.
 *
 * Locked and hidden records are not picked up at all. That is what `locked` is for, and dragging
 * something you cannot see is a way to lose it.
 */
export class StaffageTransformSubject<T extends StaffageRecord> implements TransformSubject {
  private readonly originals = new Map<string, T>();
  private mode: TransformMode = 'move';

  constructor(private readonly layer: StaffageLayer<T>) {}

  supports(mode: TransformMode): boolean {
    return mode !== 'maturity';
  }

  begin(ids: readonly string[], mode: TransformMode): number {
    this.mode = mode;
    this.originals.clear();
    for (const id of ids) {
      const item = this.layer.get(id);
      if (item && isMovable(item)) this.originals.set(id, item);
    }
    return this.originals.size;
  }

  heldIds(): readonly string[] {
    return [...this.originals.keys()];
  }

  anchor(id: string): Vec3 | null {
    const original = this.originals.get(id);
    return original ? { ...original.position } : null;
  }

  move(dx: number, dz: number): void {
    for (const original of this.originals.values()) {
      this.layer.update(
        this.layer.movedTo(original, original.position.x + dx, original.position.z + dz),
      );
    }
  }

  rotate(deltaRadians: number): void {
    for (const id of this.originals.keys()) {
      const current = this.layer.get(id);
      if (current) this.layer.update(this.layer.turnedBy(current, deltaRadians));
    }
  }

  maturity(): void {
    // Unreachable: `supports` refuses the mode, so this subject is never picked up for it.
  }

  revert(): void {
    for (const [id, original] of this.originals) {
      if (this.layer.has(id)) this.layer.update(original);
    }
  }

  commit(): Command | null {
    const changes: StaffageChange<T>[] = [];
    for (const [id, before] of this.originals) {
      const after = this.layer.get(id);
      if (!after) continue;
      if (this.layer.changed(before, after)) changes.push({ before, after });
    }
    this.originals.clear();
    if (changes.length === 0) return null;

    const verb = this.mode === 'rotate' ? 'Turn' : 'Move';
    const only = changes.length === 1 ? changes[0] : undefined;
    const label = only
      ? `${verb} ${this.layer.describe(only.after)}`
      : `${verb} ${changes.length} ${this.layer.plural}`;
    // The records were already updated live during the drag, so the caller pushes this rather than
    // executing it — the same arrangement the flora subject and the plant brush both use.
    return this.layer.updateCommand(changes, label);
  }
}
